<template>
    <div>
        <!-- selectionMode="single" -->
        <DataTable

            :multiSortMeta="preSort"
            v-model:first="offset"
            :rows="numberOfRows"

            :value="tasks"
            :lazy="true"
            :paginator="true"
            :rowClass="rowClass"
            ref="dt"
            dataKey="id"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPage($event)"
            responsiveLayout="scroll"
            :rowsPerPageOptions="[5, 10, 20, 50, 100]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No tasks available. </template>
            <template #loading> Loading tasks. Please wait. </template>
            <Column field="id" header="TASK ID" style="width: 4rem" />
            <Column field="title" header="TASK TITLE" style="width: 15rem" />
            <Column field="assigned_user" header="ASSIGNED TO" style="width: 8rem" />
            <!-- <Column field="assigned_by" header="ASSIGNED BY" style="width: 7rem" /> -->
            <Column field="task_progress" header="STATUS" style="width: 8rem">
                <template #body="slotProps">
                    <span :class="'status-badge status-' + slotProps.data.task_progress?.status">{{ slotProps.data.task_progress?.name }}</span>
                </template>
            </Column>
            <Column v-if="isFeatureEnabled" field="assurance_level" header="PROGRESS TO COMPLETION" sortable style="width: 8%">
                <template #body="slotProps">
                    <span
                        v-if="assuranceLabels[slotProps.data.assurance_level] || getAssuranceLabel(slotProps.data.assurance_level)"
                        :class="'assurance-badge badge-' + slotProps.data.assurance_level" :style="assuranceColors[slotProps.data.assurance_level] ? { backgroundColor: assuranceColors[slotProps.data.assurance_level] } : {}"
                    >
                        {{ assuranceLabels[slotProps.data.assurance_level] || getAssuranceLabel(slotProps.data.assurance_level) }}
                    </span>
                </template>
            </Column>
            <Column field="task_priority" header="PRIORITY" style="width: 6rem">
                <template #body="slotProps">
                    <span :class="'priority-badge status-' + slotProps.data.task_priority?.status">{{ slotProps.data.task_priority?.name }}</span>
                </template>
            </Column>
            <!-- <Column field="created_date" header="CREATED AT" style="width: 7rem" /> -->
            <Column field="due_at" header="DUE DATE" style="width: 7rem" />
            <Column field="theme_name" header="THEME" style="width: 7rem" />
            <Column field="actions" header="ACTIONS" style="width: 7rem">
                <template #body="slotProps">
                    <span class="p-column-title">ACTIONS</span>
                    <Button v-show="slotProps.data.editable && authStore.hasPermission(UserPermission.WRITE_TASK)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editTask(slotProps.data.id)" v-tooltip="'Edit Task'" />
                    <Button v-show="authStore.hasPermission(UserPermission.READ_TASK)" icon="pi pi-eye" class="p-button-rounded p-button-info" @click="viewTaskDetails(slotProps.data.id)" v-tooltip="'View Task'" />
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script setup>
import { ref, onMounted, defineProps, computed, onBeforeMount } from 'vue';
import { storeToRefs } from 'pinia';

import { useRouter } from 'vue-router';
import { authStore, companyStore, taskStore } from '../../../../../store/global-stores';
import { UserPermission } from '@/modules/permission/enum';
import {
    buildPagination,
    getOrder,
    getPage,
    getPerPage,
    setPage,
    setPerPage,
} from '@/composables/PaginationComposable';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';
import { setCompanyPreferences } from '@/product/ams/utils';
import { getAssuranceLabel } from '@/product/ams/constants';
import { useAssuranceColors } from '@/composables/useAssuranceColors';

const props = defineProps({
    type: {
        type: String,
    },
});

const router = useRouter();

const { loggedUser } = storeToRefs(authStore);
const { params } = storeToRefs(taskStore);
const { fetchCompanyPreference } = userCompanyPreference();
const { assuranceColors, assuranceLabels } = useAssuranceColors();

// states
const loading = ref(false);
const taskParams = ref(null);
const tasks = ref([]);
const totalRecords = ref(null);
const overdueTaskScope = ref(1);

const companyId = computed(() => loggedUser.value?.company_id);

const assignedTo = computed(() => {
    return loggedUser.value.id !== 1 ? [loggedUser.value.id] : [];
});

const offset = computed(() => {
    return getPage(props.type) * getPerPage(props.type)
})

const preSort = computed(() => {

    const order = getOrder(props.type)

    return order
})

const numberOfRows = computed(() => {
    return getPerPage(props.type)
})

const isFeatureEnabled = companyStore.useFeatureAccess('assurance-level');

// creates params for fetching tasks data
const getTaskParams = () => {
    let paramValue = { ...params.value, assigned_to: assignedTo.value };
    switch (props.type) {
        case 'over-due-date':
            paramValue.sort = 'due_date desc, priority desc';
            paramValue.overdue = true;
            if (overdueTaskScope.value === 2) {
                paramValue.assigned_to = [];
            }
            break;
        case 'near-due-date':
            paramValue.sort = 'due_date asc, priority desc';
            paramValue.overdue = false;
            break;
        case 'latest-tasks':
            paramValue.sort = 'id desc, priority desc';
            break;
    }

    return paramValue;
};

onBeforeMount(async () => {
    switch (props.type) {
        case 'over-due-date':
            buildPagination(props.type, 0, 10, [
                { field: "due_date", order: -1 },
                { field: "priority", order: -1 },
            ])
            break;
        case 'near-due-date':
            buildPagination(props.type, 0, 10, [
                { field: "due_date", order: -1 },
                { field: "priority", order: -1 },
            ])
            break;
        case 'latest-tasks':
            buildPagination(props.type, 0, 10, [
                { field: "id", order: -1 },
                { field: "priority", order: -1 },
            ])
            break;
    }
})

onMounted(async () => {
    if (companyId.value) {
        const prefs = await fetchCompanyPreference(companyId.value);
        setCompanyPreferences(companyId.value, prefs);
        companyStore.companyPreferences = { ...companyStore.companyPreferences, [companyId.value]: prefs };
        if (props.type === 'over-due-date') {
            overdueTaskScope.value = Number(prefs?.overdue_task_scope) || 1;
        }
    }
    taskParams.value = getTaskParams();
    await loadTasks(props.type);
});

const rowClass = (data) => {
    const dueDate = new Date(data.due_date);
    const today = new Date();
    const nearDueDate = new Date(today);
    nearDueDate.setDate(today.getDate() + 14);
    // check if tasks which are not completed are overdue already
    if (today > dueDate && parseInt(data.progress) !== 3) {
        return [{ 'overdue-tasks': true }];
    }
    // check if task's due date is near due date i.e 14 days
    else if (dueDate.toISOString() <= nearDueDate.toISOString() && parseInt(data.progress) !== 3) {
        return [{ 'near-overdue-tasks': true }];
    }
};

const loadTasks = async (list) => {
    buildPagination(list, 0, 10, [{field: "id", order: -1}])
    loading.value = true;
    setTimeout(async () => {
        const data = await taskStore.getNonDraftTasks(list, taskParams.value);
        if (data?.data.length > 0) {
            tasks.value = data.data;
            totalRecords.value = data.total;
        }
        loading.value = false;
    }, Math.random() * 1000 + 250);
};

const onPage = (event) => {
    setPage(props.type, event.page)
    setPerPage(props.type, event.rows)

    loadTasks(props.type);
};

const editTask = (id) => {
    router.push({ name: 'TaskForm', query: { taskId: id } });
};

const viewTaskDetails = (id) => {
    const routeData = router.resolve({ name: 'TaskDetails', params: { id } });
    router.push(routeData);

    // const routeData = router.resolve({ name: 'TaskDetails', params: { id } });
    // window.open(routeData.href, '_blank');
};

</script>

<style lang="scss">
// .p-datatable .p-datatable-tbody > tr takes precedence over the following class so its set to important here
.overdue-tasks {
    background-color: #f2c5c9 !important;
    font-weight: bold;
}
.near-overdue-tasks {
    background-color: #f5ccaea8 !important;
}
.status-badge {
    border-radius: var(--border-radius);
    padding: 0.25em 0.5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.3px;

    &.status-noprogress {
        background: #ffcdd2;
        color: #c63737;
    }
    &.status-inprogress {
        background: #feedaf;
        color: #8a5340;
    }
    &.status-completed {
        background: #c8e6c9;
        color: #256029;
    }
}

.priority-badge {
    border-radius: var(--border-radius);
    padding: 0.25em 0.5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.3px;

    &.status-low {
        background: #b3e5fc;
        color: #23547b;
    }

    &.status-medium {
        background: #feedaf;
        color: #8a5340;
    }
    &.status-high {
        background: #ffcdd2;
        color: #c63737;
    }
}

.assurance-badge {
    display: inline-block;
    padding: 0.3rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 999px; /* pill-shaped */
    text-align: center;
    text-transform: capitalize;
    white-space: nowrap;
    color: white;
}

/* Level-specific colors */
.badge-inadequate {
    background-color: #d32f2f; /* Red */
}

.badge-requires_improvement {
    background-color: #c62828; /* Dark Red */
}

.badge-adequate {
    background-color: #f57c00; /* Orange */
}

.badge-good {
    background-color: #43a047; /* Light Green */
}

.badge-outstanding {
    background-color: #2e7d32; /* Dark Green */
}
</style>
